
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { PaisDDI } from '../../../models/cardapio-digital/cardapio-digital.model';
import { PAISES_DDI } from '../../data/paises-ddi.data';

/**
 * Componente reutilizavel de DDI + Telefone com mascara dinamica por pais.
 *
 * Uso basico (com mascara automatica):
 * ```html
 * <app-phone-input
 *   [(telefone)]="modelo.telefone"
 *   [(ddi)]="paisSelecionado"
 *   [label]="labels.telefone">
 * </app-phone-input>
 * ```
 *
 * Sem mascara automatica (comportamento legado):
 * ```html
 * <app-phone-input
 *   [(telefone)]="form.telefone"
 *   [(ddi)]="paisSelecionado"
 *   [autoMask]="false"
 *   [label]="labels.ddi + ' / ' + labels.telefone"
 *   [placeholder]="placeholders.telefone"
 *   (telefoneInput)="applyMask($event)">
 * </app-phone-input>
 * ```
 */
@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
})
export class PhoneInputComponent implements OnChanges, AfterViewInit {

  // === INPUTS ===

  /** Label exibido acima do campo (ex: "DDI / Telefone"). Se vazio, nao exibe label. */
  @Input() label = '';

  /** Placeholder do campo de telefone (usado quando autoMask = false) */
  @Input() placeholder = '';

  /** Pais/DDI selecionado (two-way binding) */
  @Input() ddi: PaisDDI = PAISES_DDI[0];

  /** Numero de telefone (two-way binding) */
  @Input() telefone: string | undefined = '';

  /** Indica se o campo e obrigatorio (exibe asterisco no label) */
  @Input() required = false;

  /** Classe CSS adicional para o painel do dropdown de DDI */
  @Input() panelStyleClass = '';

  /** Classe CSS adicional para o input de telefone */
  @Input() inputStyleClass = '';

  /** Estilo inline adicional para o input de telefone */
  @Input() inputStyle = 'flex: 1;';

  /** Controla se a mascara interna e aplicada automaticamente. Default: true */
  @Input() autoMask = true;

  // === OUTPUTS ===

  @Output() ddiChange = new EventEmitter<PaisDDI>();
  @Output() telefoneChange = new EventEmitter<string | undefined>();

  /** Emitido no evento nativo (input) do campo telefone — util para aplicar mascara (retrocompatibilidade) */
  @Output() telefoneInput = new EventEmitter<Event>();

  // === DADOS ===

  paises: PaisDDI[] = PAISES_DDI;

  /** Valor exibido no input (pode conter mascara). Separado do @Input telefone (digitos puros). */
  _telefoneExibido = '';

  private _fromUserInput = false;

  /** Flag para distinguir inicializacao do dropdown de interacao do usuario */
  private _initialized = false;

  // === LIFECYCLE ===

  ngAfterViewInit(): void {
    setTimeout(() => this._initialized = true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this._fromUserInput) {                                                                                                                                                                
          this._fromUserInput = false;                                                                                                                                                            
          return;                                                     
        } 

    const telefoneChange = changes['telefone'];
    const ddiChange = changes['ddi'];

    // Reaplicar mascara quando telefone ou ddi mudam externamente (autoMask ativo)
    if (this.autoMask && (telefoneChange || ddiChange)) {
      const valorAtual = this.telefone ?? '';
      const digitos = this.extrairDigitos(valorAtual);

      if (this.ddi?.mascara && digitos.length > 0) {
        const digitosTruncados = digitos.substring(0, this.ddi.maxDigitos);
        this._telefoneExibido = this.applyPhoneMask(digitosTruncados, this.ddi.mascara);
      } else {
        this._telefoneExibido = digitos;
      }
    }
  }

  // === GETTERS ===

  /** Retorna o placeholder dinamico conforme o DDI selecionado */
  get placeholderAtual(): string {
    if (this.autoMask && this.ddi?.placeholder) {
      return this.ddi.placeholder;
    }
    return this.placeholder;
  }

  // === HANDLERS ===

  onDdiChange(pais: PaisDDI): void {
    this.ddi = pais;
    this.ddiChange.emit(pais);

    // Limpar telefone ao trocar DDI quando mascara automatica esta ativa
    // Somente limpar se o componente ja foi inicializado (interacao do usuario)
    if (this.autoMask && this._initialized) {
      this.telefone = '';
      this._telefoneExibido = '';
      this.telefoneChange.emit('');
    }
  }

  onTelefoneChange(valor: string): void {
    // Quando autoMask esta ativo, a emissao e feita no onTelefoneInput
    if (!this.autoMask) {
      this.telefone = valor;
      this._telefoneExibido = valor;
      this.telefoneChange.emit(valor);
    }
  }

  onTelefoneInput(event: Event): void {
    if (this.autoMask && this.ddi?.mascara) {
      const input = event.target as HTMLInputElement;
      const digitos = this.extrairDigitos(input.value);

      // Truncar no maximo de digitos do pais
      const digitosTruncados = digitos.substring(0, this.ddi.maxDigitos);

      // Aplicar mascara
      const valorMascarado = this.applyPhoneMask(digitosTruncados, this.ddi.mascara);

      // Atualizar input visual
      input.value = valorMascarado;

      // Atualizar modelo exibido e interno
      this._telefoneExibido = valorMascarado;
      this.telefone = valorMascarado;

      this._fromUserInput = true;
      // Emitir apenas digitos para o pai
      this.telefoneChange.emit(digitosTruncados);
    }

    // SEMPRE emitir evento para retrocompatibilidade
    this.telefoneInput.emit(event);
  }

  // === METODOS PRIVADOS ===

  /**
   * Aplica mascara de telefone generica baseada no padrao do pais.
   * @param rawValue - Valor bruto (apenas digitos)
   * @param mascara - Padrao de mascara (ex: "(##) #####-####") onde # = digito
   * @returns Valor formatado com a mascara
   */
  private applyPhoneMask(rawValue: string, mascara: string): string {
    const digitos = rawValue.replace(/\D/g, '');
    let resultado = '';
    let indexDigito = 0;

    for (let i = 0; i < mascara.length && indexDigito < digitos.length; i++) {
      if (mascara[i] === '#') {
        resultado += digitos[indexDigito];
        indexDigito++;
      } else {
        resultado += mascara[i];
      }
    }

    return resultado;
  }

  /** Extrai apenas digitos de uma string */
  private extrairDigitos(valor: string): string {
    return (valor || '').replace(/\D/g, '');
  }
}
